// store-delivery-integration.js
class StoreDeliveryIntegration {
  constructor() {
    this.dbName = 'StoreLocatorDB';
    this.dbVersion = 2;
    this.db = null;
    this.currentStore = null;
    this.productInventory = null;
    this.deliveryLocationEl = document.querySelector('.delivery-location');
    this.deliveryLocationNoData = document.getElementById('delivery-location-no-data');
    this.deliveryLocationHasData = document.getElementById('delivery-location-has-data');
    this.deliveryLocationName = document.getElementById('delivery-location-name');
    this.findStoreBtn = document.getElementById('delivery-find-store-button');

    this.locationTriggerBtn = document.getElementById('location-popup-trigger');
    this.deliveryBox = document.getElementById('delivery-box');
    this.variantSelected = document.getElementById('variant-selected');

    // Check if product is eligible for Premium Shipping
    this.premiumShippingEligible = document.getElementById('is-premium-eligible');


    this.isFetchingInventory = false;
    this.lastFetchKey = null;

    this.init();
  }

  async init() {
    await this.loadCurrentStore();
    this.setupEventListeners();

    if (this.currentStore && this.variantSelected) {
      await this.checkProductAvailability();
    }

    this.updateDeliveryUI();
  }

  async loadCurrentStore() {
    try {
      if (window.getPreferredStore) {
        this.currentStore = await window.getPreferredStore();
      }
      else {
        const storeLocator = document.querySelector('store-locator-component');
        if (storeLocator && storeLocator.loadSelectedStore) {
          this.currentStore = await storeLocator.loadSelectedStore();
        }
      }

    } catch (error) {
      console.error(error);
      this.currentStore = null;
    }
  }

  async initializeDatabase() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        console.warn('IndexedDB not supported');
        resolve(null);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('IndexedDB initialization failed:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

    });
  }

  async getDatabase() {
    if (this.db) return this.db;
    await this.initializeDatabase();
    return this.db;
  }

  async getLastSearchLocation() {
    try {
      const db = await this.getDatabase();
      if (!db || !db.objectStoreNames.contains('selectedStore')) return '';

      return new Promise((resolve) => {
        const transaction = db.transaction(['selectedStore'], 'readonly');
        const store = transaction.objectStore('selectedStore');
        const request = store.get('lastSearch');

        request.onsuccess = () => {
          const location = request.result?.location;
          resolve(typeof location === 'string' && location.trim() ? location.trim() : '');
        };

        request.onerror = () => {
          resolve('');
        };
      });
    } catch (e) {
      console.error('Error loading last search location:', e);
      return '';
    }
  }

  extractShopifyLocationId(obj, visited = new Set()) {
    if (!obj || typeof obj !== 'object' || visited.has(obj)) return null;
    visited.add(obj);

    for (const key in obj) {
      const value = obj[key];

      if (typeof value === 'string' && value.startsWith('gid://shopify/Location/')) {
        return value;
      }

      if (value && typeof value === 'object') {
        const result = this.extractShopifyLocationId(value, visited);
        if (result) return result;
      }
    }

    return null;
  }

  async checkProductAvailability() {
    if (!this.currentStore || !this.variantSelected) return;
    if (this.isFetchingInventory) return;

    const variantId = this.variantSelected.value;
    const fetchKey = `${variantId}_${this.currentStore.id}`;

    if (this.lastFetchKey === fetchKey && this.productInventory) {
      return;
    }

    this.isFetchingInventory = true;
    this.lastFetchKey = fetchKey;

    try {
      const locationId = this.extractShopifyLocationId(this.currentStore);

      if (!locationId) {
        console.warn('No Shopify location ID found in currentStore');
        return;
      }

      const inventoryData = await this.fetchStoreInventory(variantId, locationId);

      if (inventoryData) {
        this.productInventory = inventoryData;
        let availabilityStatus = 'available_to_order';
        if (inventoryData.stock_override === true || inventoryData.stock_override === 1 || inventoryData.stock_override === 'true') {
          availabilityStatus = 'available_now';
        } else if (inventoryData.warehouse_quantity >= 2) {
          availabilityStatus = 'available_now';
        } else if (inventoryData.store_quantity >= 2) {
          availabilityStatus = 'available_now';
        }


        if (inventoryData.stock_override === true || inventoryData.stock_override === 1 || inventoryData.stock_override === 'true') {
          availabilityStatus = 'available_now';
        }

        this.productInventory.availability_status = availabilityStatus;
        await this.fetchShippingRates();
      }
    } catch (error) {
      console.error('Error checking product availability:', error);
    } finally {
      this.isFetchingInventory = false;
    }
  }

  async fetchStoreInventory(variantId, locationId) {
    try {
      const apiUrl = `${window.location.origin}/apps/service/shopify/cart-location-stock?variantIds=gid://shopify/ProductVariant/${variantId}&locationId=${locationId}`;

      const response = await fetch(apiUrl, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const responseData = await response.json();

      if (!responseData.success || !responseData.data) {
        throw new Error('Invalid response format');
      }

      const variantData = responseData.data[0];
      const inventoryLevel = variantData?.inventoryItem?.inventoryLevel;

      const storeQuantity = inventoryLevel?.quantities?.[0]?.quantity || 0;
      const warehouseQuantity = inventoryLevel?.warehouse?.quantity || 0;

      return {
        store_quantity: storeQuantity,
        warehouse_quantity: warehouseQuantity,
        stock_override: inventoryLevel?.stock_override,
        raw_data: responseData
      };
    } catch (error) {
      console.error('Error fetching inventory:', error);
      return {
        store_quantity: 0,
        warehouse_quantity: 0,
        stock_override: false,
      };
    }
  }

  setupEventListeners() {
    if (this.locationTriggerBtn) {
      this.locationTriggerBtn.addEventListener('click', () => {
        this.openStoreLocator();
      });
    }

    if (this.findStoreBtn) {
      this.findStoreBtn.addEventListener('click', () => {
        this.openStoreLocator();
      });
    }

    let storeChangedTimeout;
    document.addEventListener('location:open', async (event) => {
      this.openStoreLocator();
    });
    document.addEventListener('location:changed', async (event) => {
      clearTimeout(storeChangedTimeout);
      storeChangedTimeout = setTimeout(async () => {
        await this.loadCurrentStore();

        if (this.currentStore && this.variantSelected) {
          await this.checkProductAvailability();
        }

        this.updateDeliveryUI();
      }, 300);
    });

    const storeLocator = document.querySelector('store-locator-component');
    if (storeLocator) {
      let observerTimeout;
      const observer = new MutationObserver(async (mutations) => {
        clearTimeout(observerTimeout);
        observerTimeout = setTimeout(async () => {
          await this.loadCurrentStore();

          if (this.currentStore && this.variantSelected) {
            await this.checkProductAvailability();
          }

          this.updateDeliveryUI();
        }, 300);
      });

      observer.observe(storeLocator, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }
  }

  openStoreLocator() {
    const storeLocator = document.querySelector('store-locator-component');
    if (storeLocator && storeLocator.openSetStoreModal) {
      storeLocator.openSetStoreModal();
    } else {
      const headerNoStore = document.querySelector('.header-store .current-store--no-store');
      if (headerNoStore) {
        headerNoStore.click();
      }
    }
  }

  updateDeliveryUI() {
    if (!this.deliveryLocationEl) return;

    if (this.currentStore && this.currentStore.name) {
      if (this.deliveryLocationHasData) this.deliveryLocationHasData.style.display = 'flex';
      if (this.deliveryLocationNoData) this.deliveryLocationNoData.style.display = 'none';
      if (this.locationTriggerBtn) this.locationTriggerBtn.style.display = 'block';

      // if (this.deliveryLocationName) {
      //   this.deliveryLocationName.textContent = this.currentStore.name;
      // }

      if (this.deliveryBox) {
        this.deliveryBox.classList.remove('hidden');
      }

      this.renderAvailabilityMessage();
    } else {
      if (this.deliveryLocationHasData) this.deliveryLocationHasData.style.display = 'none';
      if (this.deliveryLocationNoData) this.deliveryLocationNoData.style.display = 'block';
      if (this.locationTriggerBtn) this.locationTriggerBtn.style.display = 'none';

      if (this.deliveryBox) {
        this.deliveryBox.classList.add('hidden');
      }
    }
  }

  renderAvailabilityMessage() {
    const container = document.getElementById('availability-message');
    if (!container) return;

    if (!this.productInventory) {
      container.innerHTML = '<div class="availability-loading message-main">Checking availability...</div>';
      return;
    }

    let message = '';
    let messageClass = '';

    const shippingOptions = document.getElementById('shipping-options');
    const isCollectOnly = shippingOptions && shippingOptions.dataset.collectOnly === 'true';

    if (isCollectOnly) {
      message = 'Not Available';
      messageClass = 'collect-only';
    } else {
      switch (this.productInventory.availability_status) {
        case 'available_now':
          message = 'In Stock';
          messageClass = 'available-now';
          messageIcon = '<img src="https://cdn.shopify.com/s/files/1/0732/1239/5739/files/In-Stock-Check.svg?v=1783996533" width="24" height="24" style="width:24px;height:24px;">';
          break;
        case 'available_to_order':
        default:
          message = 'Order Now';
          messageClass = 'available-to-order';
          messageToolTip = '<div class="availability-message">May not currently be in stock. Call your local store to confirm availability.</div>';
          messageIcon = '<img src="https://cdn.shopify.com/s/files/1/0778/7141/5528/files/Order-Now-Clock.svg?v=1783994481" width="24" height="24" style="width:24px;height:24px;">';
          break;
      }
    }



    const standardIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#00D00A"/>
        <path d="M7 13L10 16L17 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;


    const collectOnlyIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#ED1C24"/>
        <mask id="mask0_16649_73912" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="2" y="2" width="20" height="20">
          <rect x="2" y="2" width="20" height="20" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_16649_73912)">
          <path d="M7.33393 17.2564L6.74414 16.6667L11.4108 12L6.74414 7.33332L7.33393 6.74353L12.0006 11.4102L16.6673 6.74353L17.2571 7.33332L12.5904 12L17.2571 16.6667L16.6673 17.2564L12.0006 12.5898L7.33393 17.2564Z" fill="white"/>
        </g>
      </svg>
    `;

    container.innerHTML = `
      <div class="availability-message">
        <span class="availability-message__icon svg-wrapper">
          ${isCollectOnly ? collectOnlyIcon : standardIcon}
        </span>
        <div class="message-main">${message}</div>
      </div>
    `;
  }

  createAvailabilityContainer() {
    const deliveryBox = document.getElementById('delivery-box');
    if (!deliveryBox) return;

    const container = document.createElement('div');
    container.id = 'availability-message';
    container.className = 'availability-container';

    deliveryBox.insertBefore(container, deliveryBox.firstChild);
  }

  async fetchShippingRates() {
    if (!this.variantSelected || !this.currentStore) return;
    const variantId = this.variantSelected.value;
    const originId = this.extractShopifyLocationId(this.currentStore);
    const distance = this.currentStore.distance;

    const apiUrl = new URL(`${window.location.origin}/apps/service/shipping/rates/calculate`);
    apiUrl.searchParams.append('variantIds', variantId);
    apiUrl.searchParams.append('locationId', originId);
    const selectedPlace = window.localStorage.getItem('selectedPlace');
    if (!selectedPlace) {
      const customerLocation = await this.getLastSearchLocation();
      if (customerLocation) {
        apiUrl.searchParams.append('destinationInput', customerLocation);
      } else {
        // No location data available, cannot fetch rates
        return;
      }
    } else {
      const destination = JSON.parse(selectedPlace);
      const postalCode = destination.address_components.find(item => item.types.includes('postal_code'))?.short_name || '';
      const country = destination.address_components.find(item => item.types.includes('country'))?.short_name || '';
      const address = destination.formatted_address || '';
      const city = destination.address_components.find(item => item.types.includes('locality'))?.short_name || '';
      const state = destination.address_components.find(item => item.types.includes('administrative_area_level_1'))?.short_name || '';

      apiUrl.searchParams.append('postalCode', postalCode);
      apiUrl.searchParams.append('country', country);
      apiUrl.searchParams.append('address', address);
      apiUrl.searchParams.append('distance', distance);
      apiUrl.searchParams.append('city', city);
      apiUrl.searchParams.append('state', state);
    }

    const shippingOptions = document.getElementById('shipping-options');
    if (!shippingOptions) return;
    const skeleton = `
      <div style="margin-top:10px;width:100%;font-family:Arial,sans-serif;background:#e5e7eb;animation:pulse 1.5s infinite"><style>@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}</style><div style="display:flex;align-items:center;gap:8px;margin-bottom:16px"><div style="width:24px;height:24px;background:#e5e7eb;border-radius:4px"></div><div style="width:160px;height:24px;background:#e5e7eb;border-radius:4px"></div><div style="margin-left:auto;width:80px;height:32px;background:#e5e7eb;border-radius:6px"></div></div><div style="border:1px solid #e5e7eb;padding:16px;background:#fff"><div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px"><div><div style="width:90px;height:14px;background:#e5e7eb;border-radius:4px;margin-bottom:8px"></div><div style="width:180px;height:20px;background:#e5e7eb;border-radius:4px"></div></div><div style="width:60px;height:18px;background:#e5e7eb;border-radius:4px"></div></div><div style="display:flex;justify-content:space-between;align-items:center"><div style="display:flex;align-items:center;gap:8px"><div style="width:24px;height:24px;border-radius:50%;background:#e5e7eb"></div><div style="width:140px;height:18px;background:#e5e7eb;border-radius:4px"></div></div><div style="display:flex;align-items:center;gap:8px"><div style="width:90px;height:18px;background:#e5e7eb;border-radius:4px"></div><div style="width:16px;height:16px;background:#e5e7eb;border-radius:4px"></div></div></div></div></div>
    `
    shippingOptions.innerHTML = skeleton;
    let isCollectOnly = shippingOptions && shippingOptions.dataset.collectOnly === 'true';

    const response = await fetch(apiUrl, {
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    let responseData = await response.json();

    if (!responseData.rates) {
      throw new Error('Invalid response format');
    }

    let rates = responseData.rates;
    let message = '';
    let messageClass = '';
    let messageDescription = '';
    let messageToolTip = '';
    let messageIcon = '';
    if (!rates || !rates.length) {
      shippingOptions.setAttribute('data-collect-only', 'true');
      isCollectOnly = true;
      rates = [{
        total_price: 0,
        service_name: 'Standard Shipping',
        service_code: 'standard-shipping',
        currency: 'AUD',
        delivery_instructions: "This product is available to pick up only",
      }];
    } else {
      shippingOptions.removeAttribute('data-collect-only');
      isCollectOnly = false;
    }

    // Set variables for Standard Shipping and Premium Shipping surcharges
    let standardShippingCost = parseFloat(rates[0].total_price) / 100;    
    let premiumSurcharge = '0';

    // if Store has Premium Shipping cost, set that as the surcharge amount
    // if (this.currentStore._apiData.premiumShippingSurcharge !== undefined && this.currentStore._apiData.premiumShippingSurcharge !== null) {
    //   premiumSurcharge = this.currentStore._apiData.premiumShippingSurcharge.value;
    // }
    
    if (this.currentStore.name === 'Designer Appliances Batemans Bay' || this.currentStore.name === 'Designer Appliances Bega') {
      premiumSurcharge = '30';
    }
 
    // Add the Premium Surcharge to the Standard Shipping cost to determine the Premium Total Cost
    let premiumTotalCost = +standardShippingCost + +premiumSurcharge;
    let premiumHtml = '';    

    // Output variables to console to check
    console.log(standardShippingCost);
    console.log(premiumSurcharge);
    console.log(premiumTotalCost);
    console.log(this.currentStore);


    if (isCollectOnly) {
      message = 'Not Available';
      messageClass = 'collect-only';
      messageDescription = 'This product is available to pick up only';
    } else {
      switch (this.productInventory.availability_status) {
        case 'available_now':
          message = 'In Stock';
          messageClass = 'available-now';
          messageIcon = '<img src="https://cdn.shopify.com/s/files/1/0732/1239/5739/files/In-Stock-Check.svg?v=1783996533" width="24" height="24" style="width:24px;height:24px;">';
          messageDescription = rates[0].description;
          break;
        case 'available_to_order':
        default:
          message = 'Order Now';
          messageClass = 'available-to-order';
          messageToolTip = '<div class="availability-message">May not currently be in stock. Call your local store to confirm availability.</div>';
          messageIcon = '<img src="https://cdn.shopify.com/s/files/1/0778/7141/5528/files/Order-Now-Clock.svg?v=1783994481" width="24" height="24" style="width:24px;height:24px;">';
          messageDescription = rates[0].description;
          break;
      }
    }

    const standardIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#00D00A"/>
        <path d="M7 13L10 16L17 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    const collectOnlyIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#ED1C24"/>
        <mask id="mask0_16649_73912" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="2" y="2" width="20" height="20">
          <rect x="2" y="2" width="20" height="20" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_16649_73912)">
          <path d="M7.33393 17.2564L6.74414 16.6667L11.4108 12L6.74414 7.33332L7.33393 6.74353L12.0006 11.4102L16.6673 6.74353L17.2571 7.33332L12.5904 12L17.2571 16.6667L16.6673 17.2564L12.0006 12.5898L7.33393 17.2564Z" fill="white"/>
        </g>
      </svg>
    `;

    const statusHtml = `
      
          <span class="availability-message__icon svg-wrapper" style="width:24px;height:24px;">
            ${isCollectOnly ? collectOnlyIcon : messageIcon}
          </span>
          <div class="message-main" style="font-weight:bold;">${message}</div>
      
    `;

// If product is eligible for Premium Shipping - Create the Premium HTML object with correct details
    if (premiumSurcharge > 0 &&  this.premiumShippingEligible !== undefined && this.premiumShippingEligible !== null) {

        premiumHtml = `
        <br>
          <strong>Premium Shipping - $${premiumTotalCost}</strong><br>
            <ul style="margin: 2px;padding-left: 24px;">
              ${this.premiumShippingEligible.value}
            </ul>
        `;      
    };


    shippingOptions.innerHTML = rates.map((rate) => {
      return `
        <div class="delivery-priority-details" id="priority-delivery-details"><div class="delivery-priority-details__header">
                  <div class="delivery-priority-details__label-wrap">
                    <span class="delivery-priority-details__icon svg-wrapper"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" alt="Standard Shipping logo">
<path d="M16.25 6.875L18.75 7.8125L19.375 10.625H16.25V6.875Z" fill="white"></path>
<rect x="0.625" y="11.25" width="11.875" height="3.75" fill="#FFD100"></rect>
<path d="M12.5 6.875H15.625V11.25H19.375V15H12.5V10.625H0.625V3.75H12.5V6.875Z" fill="#FFD100"></path>
<circle cx="4.0625" cy="15.3125" r="1.5625" fill="white"></circle>
<circle cx="15.9375" cy="15.3125" r="1.5625" fill="white"></circle>
<path d="M14.0404 15.6267C14.2957 16.5166 15.1161 17.17 16.0869 17.17C17.0576 17.17 17.8781 16.5166 18.1332 15.6267H20V9.93668C20 7.71759 18.1947 5.91232 15.9756 5.91232H13.1102V2.85156H0L0 15.6267H1.98273C2.23801 16.5166 3.05847 17.17 4.02924 17.17C5 17.17 5.82047 16.5166 6.07559 15.6267H14.0404ZM18.8283 9.93668V10.1836H16.5552V7.14325C17.8511 7.4118 18.8283 8.56216 18.8283 9.93668ZM11.9385 4.02328V10.6036H1.17172V4.02328H11.9385ZM4.02908 15.9981C3.50128 15.9981 3.0719 15.5687 3.0719 15.0407C3.0719 14.5129 3.50128 14.0836 4.02908 14.0836C4.55704 14.0836 4.98642 14.5129 4.98642 15.0407C4.98642 15.5687 4.55704 15.9981 4.02908 15.9981ZM6.07559 14.455C5.82047 13.5651 4.99985 12.9118 4.02908 12.9118C3.05847 12.9118 2.23785 13.5652 1.98273 14.455H1.17172V11.7754H11.9385V14.455H6.07559ZM16.0869 15.9981C15.5589 15.9981 15.1295 15.5687 15.1295 15.0407C15.1295 14.5129 15.5591 14.0836 16.0869 14.0836C16.6147 14.0836 17.0441 14.5129 17.0441 15.0407C17.0441 15.5687 16.6147 15.9981 16.0869 15.9981ZM18.1332 14.455C17.8781 13.5651 17.0576 12.9118 16.0869 12.9118C15.1161 12.9118 14.2957 13.5652 14.0404 14.455H13.1102V7.08405H15.3833V11.3554H18.8283V14.455H18.1332Z" fill="#054F8E"></path>
</svg>
</span>
                    <span class="delivery-priority-details__label">Delivery</span>
                  </div>
                  ${!isCollectOnly ? `<span class="delivery-priority-details__badge">${rate.total_price == 0 ? 'FREE' : '$' + (parseFloat(rate.total_price) / 100)}</span>` : ''}
                </div><div class="delivery-priority-details__body">
                <div class="delivery-priority-details__status-row ${messageClass}">
                  <div class="delivery-store__name delivery-store-details__store-header">
                    <span class="delivery-store-details__store-name" style="width: 100%;">
                      <div class="delivery-store__name-value">${this.currentStore.name}</div>
                      <input type="hidden" id="delivery-from-store-code" name="delivery_from_store_code" value="${rate.store_code || this.currentStore.store_code}"/>
                    </span>
                    <span class="delivery-store-details__store-distance hidden">
                      <button class="delivery-location-button" onClick="document.dispatchEvent(new CustomEvent('location:open'))" style="margin-top: 12px;display: block;">Change</button>
                    </span>
                  </div>
                  <div class="availability-message" >
                    ${statusHtml}
                  </div>
                  ${messageToolTip}
                  

                  <button type="button" class="delivery-store-details__more-link" id="priority-delivery-more-link-${rate.service_code}"
                          onclick="
                              const content = document.getElementById('priority-delivery-more-content-${rate.service_code}');
                              const expanded = this.getAttribute('aria-expanded') === 'true';
                              content.classList.toggle('hidden');
                              this.setAttribute('aria-expanded', !expanded);
                          "
                          aria-expanded="false" aria-controls="priority-delivery-more-content">
                    View All Delivery Options
                    <span class="delivery-store-details__more-link-icon svg-wrapper">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    </span>
                  </button>
                  <div class="delivery-priority-details__more-content priority-delivery-more-content hidden" id="priority-delivery-more-content-${rate.service_code}" style="font-size: 14px;">
                    <strong>${rate.service_name} - ${rate.total_price == 0 ? 'FREE' : '$' + (parseFloat(rate.total_price) / 100)}</strong><br>
                    <ul style="margin: 2px;padding-left: 24px;">
                      <li>Delivery to your nominated address</li>
                    </ul>

                    ${premiumHtml}
                    
                  </div>
                </div>
              </div>
            </div>
      `;
    }).join('');

    this.renderAvailabilityMessage();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StoreDeliveryIntegration();
  });
} else {
  new StoreDeliveryIntegration();
}
